import AOS from 'aos';

// `iframe` matters: hCaptcha renders into one, and without it the widget is unreachable
// once the focus trap is active.
const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(', ');

/**
 * Drives the dialogs printed on wp_footer by the [aptim-modal] shortcode.
 *
 * Every handler is delegated on `document`, so it does not matter that the markup lands at
 * the very end of the body, after this script has already run.
 */
export default class Modal {
  constructor() {
    this.$open = null;
    this.lastFocused = null;
    this.init();
  }

  init() {
    $(document).on('click', '[data-aptim-modal-open]', (e) => {
      e.preventDefault();
      this.open($(e.currentTarget));
    });

    $(document).on('click', '[data-aptim-modal-close]', (e) => {
      e.preventDefault();
      this.close();
    });

    // A click on the fixed full-viewport root - never on a descendant - counts as clicking
    // outside the panel. Menu.js's document-level outside-click listener is the wrong
    // pattern here: the very click that opens the modal would bubble up and close it again.
    $(document).on('click', '.aptim-modal', (e) => {
      if (e.target === e.currentTarget) this.close();
    });

    $(document).on('keydown', (e) => {
      if (!this.$open) return;

      if (e.key === 'Escape' || e.key === 'Esc') {
        this.close();
        return;
      }

      if (e.key === 'Tab') this.trapFocus(e);
    });
  }

  open($trigger) {
    const id = $trigger.attr('data-aptim-modal-open');
    const $modal = $(document.getElementById(id));

    if (!$modal.length) {
      // The dialog is printed on wp_footer by the shortcode, so a missing one means the
      // shortcode never ran on this request. The usual cause is Elementor's element cache
      // (Elementor > Settings > Performance) serving the widget's already-expanded HTML from
      // post meta, which leaves the trigger behind without its dialog.
      console.warn(`Aptim modal "${id}" is not in the document.`);
      $trigger.attr('hidden', 'hidden');
      return;
    }

    if (this.$open) {
      if (this.$open.is($modal)) return;
      this.close();
    }

    // .nav-mobile sits at z-index 10100 and paints its own blurred body::after backdrop.
    $('#nav-mobile').removeClass('is-open');
    $('body').removeClass('mobile-nav-is-open');

    this.lastFocused = document.activeElement;
    this.$open = $modal;

    this.lockScroll();
    $modal.addClass('is-open').attr('aria-hidden', 'false');

    // One frame later the root is no longer `visibility: hidden`. Until then .focus() is a
    // silent no-op and slick cannot measure the subtree.
    window.requestAnimationFrame(() => {
      const $focusable = this.focusable($modal);
      const $target = $focusable.length
        ? $focusable.first()
        : $modal.find('.aptim-modal__panel');

      $target.trigger('focus');

      if (typeof $.fn.slick === 'function') {
        $modal.find('.slick-slider').slick('setPosition');
      }

      AOS.refresh();
    });
  }

  close() {
    if (!this.$open) return;

    const $modal = this.$open;
    this.$open = null;

    $modal.removeClass('is-open').attr('aria-hidden', 'true');
    this.unlockScroll();

    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus();
    }

    this.lastFocused = null;
  }

  /**
   * Hiding the page's scrollbar widens the viewport, which shifts the content underneath.
   * Reserve the same width as padding so it stays put. Overlay-scrollbar platforms (macOS,
   * touch) measure 0 and are unaffected.
   */
  lockScroll() {
    const gutter = window.innerWidth - document.documentElement.clientWidth;

    document.documentElement.style.setProperty('--aptim-scrollbar-gutter', `${gutter}px`);
    $('body').addClass('aptim-modal-is-open');
  }

  unlockScroll() {
    $('body').removeClass('aptim-modal-is-open');
    document.documentElement.style.removeProperty('--aptim-scrollbar-gutter');
  }

  focusable($modal) {
    return $modal.find(FOCUSABLE).filter(':visible');
  }

  trapFocus(e) {
    const $focusable = this.focusable(this.$open);

    if (!$focusable.length) {
      e.preventDefault();
      return;
    }

    const first = $focusable[0];
    const last = $focusable[$focusable.length - 1];

    if (!this.$open[0].contains(e.target)) {
      e.preventDefault();
      first.focus();
    } else if (e.shiftKey && e.target === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && e.target === last) {
      e.preventDefault();
      first.focus();
    }
  }
}
